'use client';

import React from 'react';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useTranslations } from '@/hooks/useTranslations';
import type { GameProviderEntry } from '@/lib/cms';
import { getContentImageMeta } from '@/lib/image-meta';
import { getProviderLogoUrl } from '@/lib/provider-image';
import { ProviderLogoWithFallback } from '@/components/ProviderLogoWithFallback';
import { MotionStagger } from '@/components/MotionStagger';

const PROVIDERS_PER_PAGE = 10; // 2 rows of 5 on lg
const COLS_PER_ROW = 5;
const INITIAL_COUNT = COLS_PER_ROW * 2;

function getLogoUrl(provider: GameProviderEntry): string | null {
  const logo = provider.logo;
  if (logo && typeof logo === 'object') {
    const data = logo as { data?: { attributes?: { url?: string } }; url?: string };
    const url = data.url ?? data.data?.attributes?.url;
    if (typeof url === 'string' && url) return url;
  }
  const slug = provider.slug?.trim();
  if (slug) return getProviderLogoUrl(slug);
  return null;
}

type Props = {
  providers: GameProviderEntry[];
  cmsBase: string;
};

export function ProvidersGrid({ providers, cmsBase }: Props) {
  const { t } = useTranslations();
  const [displayCount, setDisplayCount] = React.useState(INITIAL_COUNT);
  const visibleProviders = providers.slice(0, displayCount);
  const hasMore = displayCount < providers.length;

  return (
    <>
      <MotionStagger className="grid grid-cols-2 gap-6 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5" staggerStep={0.05}>
        {visibleProviders.map((provider, index) => {
          const logoUrl = getLogoUrl(provider);
          const slug = provider.slug ?? '';
          const name = provider.name ?? 'Unknown';
          const gamesHref = slug ? `/games?provider=${encodeURIComponent(slug)}` : '/games';
          const infoHref = slug ? `/game-providers/${encodeURIComponent(slug)}` : '/game-providers';

          return (
            <div
              key={provider.documentId ?? provider.id ?? provider.slug ?? index}
              className="group flex flex-col overflow-hidden rounded-2xl border border-white/5 bg-[#1e2235]/50 transition-all duration-300 hover:border-[#ffb74d]/40 hover:bg-[#1e2235] hover:shadow-lg"
            >
              <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden bg-black/30 p-3">
                {logoUrl ? (
                  (() => {
                    const imageMeta = getContentImageMeta({
                      pageTitle: t('nav.gameProviders'),
                      imageNumber: Number(provider.sortOrder ?? 0) > 0 ? Number(provider.sortOrder) : provider.id,
                    });
                    const resolvedLogoUrl =
                      logoUrl.startsWith('http') || logoUrl.startsWith('/')
                        ? logoUrl
                        : `${cmsBase}${logoUrl}`;
                    return (
                      <ProviderLogoWithFallback
                        src={resolvedLogoUrl}
                        name={name}
                        alt={imageMeta.alt}
                        title={imageMeta.title}
                        className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                      />
                    );
                  })()
                ) : (
                  <ProviderLogoWithFallback
                    name={name}
                    src=""
                    alt={name}
                    className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e2235] via-transparent to-transparent opacity-60" />
              </div>
              <div className="flex flex-1 flex-col p-4">
                <h2 className="text-center font-bold text-white transition-colors group-hover:text-[#ffb74d]">
                  {name}
                </h2>
                <div className="mt-2 flex flex-wrap justify-center gap-2">
                  {provider.established != null && (
                    <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-slate-400">
                      {provider.established}
                    </span>
                  )}
                  {provider.gamesCount != null && (
                    <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] text-slate-400">
                      {provider.gamesCount} slots
                    </span>
                  )}
                </div>
                {provider.description && (
                  <p className="mt-2 line-clamp-2 text-center text-xs text-slate-400">
                    {provider.description}
                  </p>
                )}
              </div>
              <div className="flex gap-2 p-4 pt-0">
                <LocalizedLink
                  href={gamesHref}
                  className="flex-1 rounded-lg bg-[#ffb74d] py-2 text-center text-sm font-bold text-[#4a2800] transition-colors hover:bg-amber-400"
                >
                  Games
                </LocalizedLink>
                <LocalizedLink
                  href={infoHref}
                  className="flex-1 rounded-lg border border-white/20 bg-white/5 py-2 text-center text-sm font-medium text-white transition-colors hover:bg-white/10"
                >
                  Info
                </LocalizedLink>
              </div>
            </div>
          );
        })}
      </MotionStagger>
      {hasMore && (
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => setDisplayCount((c) => Math.min(c + PROVIDERS_PER_PAGE, providers.length))}
            className="rounded-xl border border-white/20 bg-white/5 px-8 py-4 font-medium text-white transition-colors hover:bg-white/10"
          >
            {t('games.loadMore')}
          </button>
        </div>
      )}
    </>
  );
}
